import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { StatCard, StatusBadge, money } from "../../components/DashWidgets";
import { Button } from "../../components/ui/button";
import { affiliateStats } from "../../lib/api";
import { TeamTree } from "../../components/TeamTree";
import { PayoutsPanel } from "../../components/PayoutsPanel";
import { useTabParam } from "../../lib/useTabParam";

export default function AffiliateDashboard() {
  const [tab, setTab] = useTabParam("overview");
  const [stats, setStats] = useState({ referral_code: "", referred_leads: 0, referred_users: 0, earnings: 0, commissions: [] });
  const [copied, setCopied] = useState(false);

  useEffect(() => { affiliateStats().then(setStats).catch(() => {}); }, []);

  const link = `${window.location.origin}/register?ref=${stats.referral_code}`;
  const copy = () => {
    const done = () => { setCopied(true); toast.success("Referral link copied!"); setTimeout(() => setCopied(false), 2000); };
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(link).then(done).catch(() => fallbackCopy(link, done));
      } else {
        fallbackCopy(link, done);
      }
    } catch {
      fallbackCopy(link, done);
    }
  };

  const fallbackCopy = (text, done) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      done();
    } catch {
      toast.error("Copy failed — please copy the link manually");
    }
  };

  return (
    <DashboardLayout tab={tab} setTab={setTab}>
      {tab === "overview" && (
        <div className="space-y-6" data-testid="affiliate-overview">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard icon="Users" label="Referred Signups" value={stats.referred_users} testid="aff-stat-users" />
            <StatCard icon="UserPlus" label="Referred Leads" value={stats.referred_leads} testid="aff-stat-leads" />
            <StatCard icon="Coins" label="Total Earnings" value={money(stats.earnings)} accent="green" testid="aff-stat-earnings" />
          </div>
          <ReferralCard link={link} copy={copy} copied={copied} code={stats.referral_code} />
        </div>
      )}
      {tab === "referral" && <ReferralCard link={link} copy={copy} copied={copied} code={stats.referral_code} full />}
      {tab === "team" && <TeamTree />}
      {tab === "payouts" && <PayoutsPanel />}
      {tab === "earnings" && (
        <div data-testid="affiliate-earnings">
          <h2 className="font-heading text-2xl font-bold text-white mb-5">Earnings</h2>
          <StatCard icon="Coins" label="Total Earnings" value={money(stats.earnings)} accent="green" />
          <div className="mt-6 space-y-3">
            {stats.commissions.length === 0 ? <p className="text-slate-400">No earnings yet. Share your link to start earning!</p> :
              stats.commissions.map((c) => (
                <div key={c.id} className="card-dark rounded-xl p-4 flex items-center justify-between">
                  <div className="text-white font-mono-fin font-bold">{money(c.amount)}</div>
                  <StatusBadge status={c.status} />
                </div>
              ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function ReferralCard({ link, copy, copied, code, full }) {
  return (
    <div className="card-dark rounded-2xl p-6 gold-glow" data-testid="referral-card">
      <div className="text-xs uppercase tracking-widest text-gold font-semibold">Your Referral Code</div>
      <div className="font-mono-fin text-3xl font-extrabold text-white mt-1">{code}</div>
      <div className="mt-4">
        <div className="text-sm text-slate-400 mb-2">Share this link — earn commission when your referrals get a loan approved.</div>
        <div className="flex gap-2">
          <div className="flex-1 bg-obsidian-900/60 border border-white/10 rounded-lg px-3 py-2.5 text-slate-300 text-sm truncate" data-testid="referral-link">{link}</div>
          <Button onClick={copy} data-testid="copy-referral-button" className="gold-gradient text-obsidian-900 font-bold rounded-lg px-4">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      {full && (
        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          {["Share your link", "They register & apply", "You earn commission"].map((s, i) => (
            <div key={s} className="bg-obsidian-900/60 rounded-xl p-4">
              <div className="font-mono-fin text-gold font-bold">0{i + 1}</div>
              <div className="text-slate-300 text-sm mt-1">{s}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
